export type Language = 'en' | 'fr';

export const translations = {
  en: {
    nav: {
      home: "Home",
      about: "About Us",
      departments: "Departments",
      doctors: "Doctors",
      appointment: "Appointment",
      emergency: "Emergency",
      patients: "Patient Info",
      news: "News",
      contact: "Contact",
    },
    common: {
      bookAppointment: "Book Appointment",
      emergency: "Emergency",
      contactUs: "Contact Us",
      learnMore: "Learn More",
      viewAll: "View All",
      search: "Search",
      filter: "Filter by specialty",
      submit: "Submit",
      required: "Required",
      loading: "Loading...",
      close: "Close",
      readMore: "Read More",
      allSpecialties: "All Specialties",
    },
    home: {
      heroTitle: "Your Health, Our Priority",
      heroSubtitle: "Hôpital Régional de Bafoussam provides world-class medical care to the people of the West Region of Cameroon. Expert doctors, modern facilities, compassionate service.",
      emergencyBanner: "Emergency? Call us 24/7:",
      whyChooseUs: "Why Choose Us",
      whyDesc: "We combine medical excellence with compassionate care to serve our community",
      servicesTitle: "Our Key Services",
      servicesDesc: "Comprehensive healthcare services under one roof",
      doctorsTitle: "Meet Our Specialists",
      doctorsDesc: "Experienced and dedicated medical professionals committed to your wellbeing",
      newsTitle: "Health News & Awareness",
      newsDesc: "Stay informed with our latest health updates and announcements",
      testimonialsTitle: "Patient Testimonials",
      statsPatients: "Patients Served",
      statsDoctors: "Medical Specialists",
      statsDepts: "Departments",
      statsYears: "Years of Service",
      quickAccess: "Quick Access",
      quick1: "Find a Doctor",
      quick2: "Book Appointment",
      quick3: "Emergency Care",
      quick4: "Patient Info",
      why1Title: "Expert Medical Team",
      why1Desc: "Over 80 qualified specialists across all major medical disciplines",
      why2Title: "Modern Equipment",
      why2Desc: "State-of-the-art diagnostic and treatment technology",
      why3Title: "24/7 Emergency Care",
      why3Desc: "Round-the-clock emergency services and ambulance response",
      why4Title: "Patient-Centered Care",
      why4Desc: "We put patients first in everything we do",
      why5Title: "Affordable Services",
      why5Desc: "Quality healthcare accessible to all, with flexible payment options",
      why6Title: "Community Commitment",
      why6Desc: "Deeply rooted in serving the West Region of Cameroon",
    },
    about: {
      title: "About Hôpital Régional de Bafoussam",
      subtitle: "Serving the West Region of Cameroon with excellence since 1978",
      missionTitle: "Our Mission",
      mission: "To provide accessible, quality healthcare services to all people of the West Region, regardless of their social or economic condition, with compassion and medical excellence.",
      visionTitle: "Our Vision",
      vision: "To become the reference center for healthcare in the West Region and Central Africa, recognized for the quality of its care, the competence of its staff, and its commitment to innovation.",
      valuesTitle: "Our Values",
      historyTitle: "Our History",
      historyText: "Founded in 1978 by the Cameroonian government, Hôpital Régional de Bafoussam has been the main public healthcare facility for the West Region. Over the decades, we have grown from a small district hospital into a modern regional medical center equipped with advanced diagnostic tools, specialized departments, and a highly trained medical team. We have served over 500,000 patients and continue to expand our services to meet the region's growing healthcare needs.",
      leadershipTitle: "Message from the Director",
      leadershipName: "Dr. Emmanuel Nkouam",
      leadershipRole: "Medical Director",
      leadershipMessage: "At Hôpital Régional de Bafoussam, our commitment is simple: to put the patient at the center of everything we do. We have invested heavily in training our staff, modernizing our equipment, and expanding our services. Our doors are open to all who need care, and we are proud to serve this great region of Cameroon.",
      values: ["Compassion", "Excellence", "Integrity", "Accessibility", "Innovation", "Community"],
      communityTitle: "Our Commitment to Cameroon",
      communityText: "We actively participate in national health campaigns, vaccination programs, maternal health initiatives, and disease prevention programs. We work closely with the Ministry of Public Health and international health organizations to ensure the highest standards of care.",
    },
    departments: {
      title: "Our Departments & Services",
      subtitle: "Comprehensive medical care across all specialties",
    },
    doctors: {
      title: "Our Medical Team",
      subtitle: "Qualified, experienced, and dedicated to your health",
      searchPlaceholder: "Search by name or specialty...",
      availability: "Available:",
      bookWith: "Book Appointment",
    },
    appointment: {
      title: "Book an Appointment",
      subtitle: "Fill in the form below and our team will confirm your appointment within 24 hours",
      fullName: "Full Name",
      phone: "Phone Number",
      email: "Email Address",
      department: "Department",
      doctor: "Preferred Doctor",
      date: "Preferred Date",
      message: "Additional Message",
      messagePlaceholder: "Please describe your symptoms or reason for visit...",
      submitBtn: "Request Appointment",
      successTitle: "Appointment Request Received!",
      successMsg: "Thank you! We will contact you within 24 hours to confirm your appointment.",
      selectDept: "Select a Department",
      selectDoctor: "Select a Doctor (Optional)",
    },
    emergency: {
      title: "Emergency Services",
      subtitle: "Available 24 hours a day, 7 days a week for all medical emergencies",
      mainNumber: "Main Emergency Line",
      ambulance: "Ambulance Service",
      whatToDo: "What to Do in an Emergency",
      step1: "Call our emergency number immediately",
      step2: "Stay calm and provide your location clearly",
      step3: "Do not move the injured person unless absolutely necessary",
      step4: "Keep the line open and follow instructions",
      emergencyTypes: "We Handle All Emergencies",
      location: "Find Us At:",
      urgentCare: "Urgent Care Notice",
      urgentDesc: "For life-threatening emergencies, go directly to our Emergency entrance. Our team is available 24/7.",
    },
    patients: {
      title: "Patient Information",
      subtitle: "Everything you need to know for a smooth hospital experience",
      visitingHours: "Visiting Hours",
      visitingText: "General ward visiting hours are 10:00 AM – 12:00 PM and 4:00 PM – 6:00 PM. ICU visits are restricted to immediate family only from 11:00 AM – 12:00 PM.",
      admission: "Admission Process",
      admissionSteps: ["Present your national ID or passport", "Complete the admission form at reception", "Pay the initial consultation fee", "Wait for triage by a nurse", "You will be directed to the appropriate ward or doctor"],
      consultation: "Consultation Process",
      consultationSteps: ["Walk-in or scheduled appointment", "Register at the reception desk", "Wait for your name to be called", "See the doctor for consultation", "Collect prescriptions and instructions"],
      payment: "Payment & Billing",
      paymentText: "We accept cash payments in CFA Francs. Mobile money (MTN Mobile Money, Orange Money) is accepted. Social insurance (CNPS) is honored. Payment plans are available for eligible patients.",
      rights: "Patient Rights & Responsibilities",
      rightsList: ["Right to quality care without discrimination", "Right to information about your diagnosis", "Right to privacy and confidentiality", "Right to refuse treatment", "Responsibility to provide accurate medical history", "Responsibility to follow medical advice"],
      faq: "Frequently Asked Questions",
      faqItems: [
        { q: "Do I need an appointment?", a: "Walk-ins are welcome, but appointments are recommended to reduce waiting time." },
        { q: "What documents should I bring?", a: "Bring your national ID, health insurance card (if applicable), and any previous medical records." },
        { q: "Is the hospital open on weekends?", a: "Yes, emergency services are available 24/7. Outpatient consultations are available Saturday 8AM–2PM." },
        { q: "What payment methods are accepted?", a: "We accept cash (CFA Francs), MTN Mobile Money, Orange Money, and CNPS insurance." },
      ],
    },
    news: {
      title: "Health News & Announcements",
      subtitle: "Stay informed with our latest updates and health awareness campaigns",
    },
    contact: {
      title: "Contact Us",
      subtitle: "We're here to help — reach out to us anytime",
      address: "Address",
      addressValue: "Avenue de l'Indépendance, Bafoussam, Région de l'Ouest, Cameroun",
      phone: "Phone",
      email: "Email",
      emailValue: "info@hopital-bafoussam.cm",
      hours: "Opening Hours",
      sendMessage: "Send Us a Message",
      yourName: "Your Name",
      yourEmail: "Your Email",
      subject: "Subject",
      yourMessage: "Your Message",
      sendBtn: "Send Message",
      successMsg: "Message sent successfully! We will reply within 48 hours.",
      mapTitle: "Find Us",
    },
    footer: {
      description: "Hôpital Régional de Bafoussam — Providing quality healthcare to the people of West Cameroon since 1978.",
      quickLinks: "Quick Links",
      contact: "Contact",
      hours: "Opening Hours",
      emergency: "Emergency",
      rights: "All rights reserved.",
      mondayFriday: "Monday – Friday: 7:00 AM – 6:00 PM",
      saturday: "Saturday: 8:00 AM – 2:00 PM",
      sunday: "Sunday & Holidays: Emergency Only",
      emergencyAlways: "Emergency: 24/7 — (+237) 233 44 10 00",
    },
  },
  fr: {
    nav: {
      home: "Accueil",
      about: "À Propos",
      departments: "Départements",
      doctors: "Médecins",
      appointment: "Rendez-vous",
      emergency: "Urgences",
      patients: "Info Patient",
      news: "Actualités",
      contact: "Contact",
    },
    common: {
      bookAppointment: "Prendre Rendez-vous",
      emergency: "Urgences",
      contactUs: "Nous Contacter",
      learnMore: "En Savoir Plus",
      viewAll: "Voir Tout",
      search: "Rechercher",
      filter: "Filtrer par spécialité",
      submit: "Soumettre",
      required: "Requis",
      loading: "Chargement...",
      close: "Fermer",
      readMore: "Lire Plus",
      allSpecialties: "Toutes les Spécialités",
    },
    home: {
      heroTitle: "Votre Santé, Notre Priorité",
      heroSubtitle: "L'Hôpital Régional de Bafoussam offre des soins médicaux de qualité aux populations de la Région de l'Ouest du Cameroun. Médecins experts, installations modernes, service bienveillant.",
      emergencyBanner: "Urgence ? Appelez-nous 24h/24 :",
      whyChooseUs: "Pourquoi Nous Choisir",
      whyDesc: "Nous combinons excellence médicale et soins bienveillants pour servir notre communauté",
      servicesTitle: "Nos Services Clés",
      servicesDesc: "Des soins de santé complets sous un même toit",
      doctorsTitle: "Rencontrez Nos Spécialistes",
      doctorsDesc: "Des professionnels de santé expérimentés et dévoués à votre bien-être",
      newsTitle: "Actualités Santé & Sensibilisation",
      newsDesc: "Restez informé de nos dernières nouvelles et annonces de santé",
      testimonialsTitle: "Témoignages de Patients",
      statsPatients: "Patients Servis",
      statsDoctors: "Spécialistes Médicaux",
      statsDepts: "Départements",
      statsYears: "Années de Service",
      quickAccess: "Accès Rapide",
      quick1: "Trouver un Médecin",
      quick2: "Prendre Rendez-vous",
      quick3: "Soins d'Urgence",
      quick4: "Info Patient",
      why1Title: "Équipe Médicale Experte",
      why1Desc: "Plus de 80 spécialistes qualifiés dans toutes les disciplines médicales",
      why2Title: "Équipements Modernes",
      why2Desc: "Technologies de diagnostic et de traitement à la pointe",
      why3Title: "Urgences 24h/24",
      why3Desc: "Services d'urgence et ambulance disponibles en permanence",
      why4Title: "Soins Centrés Patient",
      why4Desc: "Nous plaçons les patients au premier rang de tout ce que nous faisons",
      why5Title: "Services Abordables",
      why5Desc: "Des soins de qualité accessibles à tous, avec des options de paiement flexibles",
      why6Title: "Engagement Communautaire",
      why6Desc: "Profondément enracinés dans le service de la Région de l'Ouest du Cameroun",
    },
    about: {
      title: "À Propos de l'Hôpital Régional de Bafoussam",
      subtitle: "Au service de la Région de l'Ouest du Cameroun avec excellence depuis 1978",
      missionTitle: "Notre Mission",
      mission: "Fournir des services de santé accessibles et de qualité à toutes les populations de la Région de l'Ouest, sans distinction sociale ou économique, avec compassion et excellence médicale.",
      visionTitle: "Notre Vision",
      vision: "Devenir le centre de référence en matière de soins de santé dans la Région de l'Ouest et en Afrique Centrale, reconnu pour la qualité de ses soins, la compétence de son personnel et son engagement envers l'innovation.",
      valuesTitle: "Nos Valeurs",
      historyTitle: "Notre Histoire",
      historyText: "Fondé en 1978 par le gouvernement camerounais, l'Hôpital Régional de Bafoussam est le principal établissement de santé public de la Région de l'Ouest. Au fil des décennies, nous sommes passés d'un petit hôpital de district à un centre médical régional moderne équipé d'outils de diagnostic avancés, de départements spécialisés et d'une équipe médicale hautement qualifiée. Nous avons servi plus de 500 000 patients et continuons d'étendre nos services.",
      leadershipTitle: "Message du Directeur",
      leadershipName: "Dr. Emmanuel Nkouam",
      leadershipRole: "Directeur Médical",
      leadershipMessage: "À l'Hôpital Régional de Bafoussam, notre engagement est simple : placer le patient au centre de tout ce que nous faisons. Nous avons investi massivement dans la formation de notre personnel, la modernisation de nos équipements et l'extension de nos services. Nos portes sont ouvertes à tous ceux qui ont besoin de soins.",
      values: ["Compassion", "Excellence", "Intégrité", "Accessibilité", "Innovation", "Communauté"],
      communityTitle: "Notre Engagement envers le Cameroun",
      communityText: "Nous participons activement aux campagnes de santé nationales, aux programmes de vaccination, aux initiatives de santé maternelle et aux programmes de prévention des maladies. Nous travaillons en étroite collaboration avec le Ministère de la Santé Publique et les organisations internationales.",
    },
    departments: {
      title: "Nos Départements & Services",
      subtitle: "Des soins médicaux complets dans toutes les spécialités",
    },
    doctors: {
      title: "Notre Équipe Médicale",
      subtitle: "Qualifiés, expérimentés et dévoués à votre santé",
      searchPlaceholder: "Rechercher par nom ou spécialité...",
      availability: "Disponible :",
      bookWith: "Prendre Rendez-vous",
    },
    appointment: {
      title: "Prendre Rendez-vous",
      subtitle: "Remplissez le formulaire ci-dessous et notre équipe confirmera votre rendez-vous dans les 24 heures",
      fullName: "Nom Complet",
      phone: "Numéro de Téléphone",
      email: "Adresse Email",
      department: "Département",
      doctor: "Médecin Préféré",
      date: "Date Préférée",
      message: "Message Supplémentaire",
      messagePlaceholder: "Veuillez décrire vos symptômes ou la raison de votre visite...",
      submitBtn: "Demander un Rendez-vous",
      successTitle: "Demande de Rendez-vous Reçue !",
      successMsg: "Merci ! Nous vous contacterons dans les 24 heures pour confirmer votre rendez-vous.",
      selectDept: "Sélectionner un Département",
      selectDoctor: "Sélectionner un Médecin (Optionnel)",
    },
    emergency: {
      title: "Services d'Urgence",
      subtitle: "Disponibles 24h/24, 7j/7 pour toutes les urgences médicales",
      mainNumber: "Ligne d'Urgence Principale",
      ambulance: "Service d'Ambulance",
      whatToDo: "Que Faire en Cas d'Urgence",
      step1: "Appelez immédiatement notre numéro d'urgence",
      step2: "Restez calme et donnez clairement votre position",
      step3: "Ne déplacez pas la personne blessée sauf si absolument nécessaire",
      step4: "Gardez la ligne ouverte et suivez les instructions",
      emergencyTypes: "Nous Traitons Toutes les Urgences",
      location: "Trouvez-nous à :",
      urgentCare: "Avis de Soins Urgents",
      urgentDesc: "Pour les urgences vitales, rendez-vous directement à notre entrée Urgences. Notre équipe est disponible 24h/24.",
    },
    patients: {
      title: "Information Patient",
      subtitle: "Tout ce que vous devez savoir pour une expérience hospitalière fluide",
      visitingHours: "Heures de Visite",
      visitingText: "Les heures de visite en service général sont 10h00 – 12h00 et 16h00 – 18h00. Les visites en soins intensifs sont réservées à la famille immédiate de 11h00 à 12h00.",
      admission: "Processus d'Admission",
      admissionSteps: ["Présentez votre carte d'identité nationale ou passeport", "Remplissez le formulaire d'admission à la réception", "Payez les frais de consultation initiale", "Attendez le triage par une infirmière", "Vous serez orienté vers le service ou le médecin approprié"],
      consultation: "Processus de Consultation",
      consultationSteps: ["Visite spontanée ou rendez-vous planifié", "Inscrivez-vous au bureau de la réception", "Attendez que votre nom soit appelé", "Consultez le médecin", "Récupérez les ordonnances et instructions"],
      payment: "Paiement & Facturation",
      paymentText: "Nous acceptons les paiements en espèces en Francs CFA. Le mobile money (MTN Mobile Money, Orange Money) est accepté. L'assurance sociale (CNPS) est honorée. Des plans de paiement sont disponibles pour les patients éligibles.",
      rights: "Droits & Responsabilités du Patient",
      rightsList: ["Droit à des soins de qualité sans discrimination", "Droit à l'information sur votre diagnostic", "Droit à la vie privée et à la confidentialité", "Droit de refuser un traitement", "Responsabilité de fournir des antécédents médicaux exacts", "Responsabilité de suivre les conseils médicaux"],
      faq: "Questions Fréquemment Posées",
      faqItems: [
        { q: "Ai-je besoin d'un rendez-vous ?", a: "Les visites spontanées sont les bienvenues, mais les rendez-vous sont recommandés pour réduire les temps d'attente." },
        { q: "Quels documents dois-je apporter ?", a: "Apportez votre carte d'identité nationale, votre carte d'assurance maladie (le cas échéant) et tous vos antécédents médicaux." },
        { q: "L'hôpital est-il ouvert le week-end ?", a: "Oui, les services d'urgence sont disponibles 24h/24. Les consultations ambulatoires sont disponibles le samedi de 8h à 14h." },
        { q: "Quels modes de paiement sont acceptés ?", a: "Nous acceptons les espèces (Francs CFA), MTN Mobile Money, Orange Money et l'assurance CNPS." },
      ],
    },
    news: {
      title: "Actualités Santé & Annonces",
      subtitle: "Restez informé de nos dernières mises à jour et campagnes de sensibilisation",
    },
    contact: {
      title: "Nous Contacter",
      subtitle: "Nous sommes là pour vous aider — contactez-nous à tout moment",
      address: "Adresse",
      addressValue: "Avenue de l'Indépendance, Bafoussam, Région de l'Ouest, Cameroun",
      phone: "Téléphone",
      email: "Email",
      emailValue: "info@hopital-bafoussam.cm",
      hours: "Heures d'Ouverture",
      sendMessage: "Envoyez-nous un Message",
      yourName: "Votre Nom",
      yourEmail: "Votre Email",
      subject: "Sujet",
      yourMessage: "Votre Message",
      sendBtn: "Envoyer le Message",
      successMsg: "Message envoyé avec succès ! Nous vous répondrons sous 48 heures.",
      mapTitle: "Nous Trouver",
    },
    footer: {
      description: "Hôpital Régional de Bafoussam — Fournissant des soins de qualité aux populations de l'Ouest Cameroun depuis 1978.",
      quickLinks: "Liens Rapides",
      contact: "Contact",
      hours: "Heures d'Ouverture",
      emergency: "Urgences",
      rights: "Tous droits réservés.",
      mondayFriday: "Lundi – Vendredi : 7h00 – 18h00",
      saturday: "Samedi : 8h00 – 14h00",
      sunday: "Dimanche & Jours Fériés : Urgences Uniquement",
      emergencyAlways: "Urgences : 24h/24 — (+237) 233 44 10 00",
    },
  },
} as const;

export type TranslationKeys = typeof translations.en;
